import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { addDays, daysBetween, relativeDay, shortDate, dayOfTimestamp } from '../../utils/taskDates';
import { firstName, userName } from '../../utils/taskUsers';
import { descendantIds } from '../../utils/taskTree';
import { LINK_NOUN, linkUrl, projectItemUrl } from '../../utils/taskLinks';
import { useToast } from '../../context/ToastContext';
import { kindIcon, planChips } from '../projects/planChips';
import SchedulePanel from '../projects/SchedulePanel';
import DependsPanel from '../projects/DependsPanel';
import MaterialPanel from '../projects/MaterialPanel';
import ValuePanel from '../projects/ValuePanel';
import { MATERIAL_STATUS, isMaterial, isRanked, materialTotal, money, valueLabel, valueOf } from '../../utils/projectValue';
import { planClasses, rangeLabel } from '../../utils/projectPlan';
import ProgressCheck from './ProgressCheck';
import Popover from './Popover';
import TaskIcon from './TaskIcon';
import { BubbleStack, UserPicker } from './UserBubble';

export interface ItemRowProps {
  item: TaskItem;
  depth: number;
  hasChildren: boolean;
  readOnly: boolean;
  isNew: boolean;
  flash: boolean;
  dragging: boolean;
  draggable: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
  rowRef?: (el: HTMLLIElement | null) => void;
  focusCaret: 'start' | 'end' | null;
  onFocused: () => void;
  canIndent: boolean;
  canOutdent: boolean;
  onEnter: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onRemoveEmpty: () => void;
  onFocusPrev: () => void;
  onFocusNext: () => void;
  onAddChild: () => void;
  /** A sub-item can go under this line (false at the deepest level the card or list allows). */
  canAddChild?: boolean;
  /** Lines under this one that the card doesn't show (a project branch on Lists). */
  moreBelow?: { count: number; onOpen: () => void };
  /** Folding, where the list allows it (a project's Outline). `has` = it has sub-items to fold. */
  fold?: { has: boolean; collapsed: boolean; hidden: number; onToggle: () => void };
}

const QUARTERS = [0, 25, 50, 75, 100];
type Panel = 'main' | 'note' | 'due' | 'waiting' | 'plan' | 'deps' | 'material' | 'value';

const ItemRow: React.FC<ItemRowProps> = (props) => {
  const { item, depth, hasChildren, readOnly, isNew, flash, dragging, draggable } = props;
  const { users, me, today, items, lists, costs, updateItem, deleteItem, weeklyListId, openListDialog } = useTasks();
  const navigate = useNavigate();
  // A week-list line linked from another list (a warehouse request, or
  // anything added with "Add to MPP's week list") opens that list in a dialog,
  // with the line pulsing, rather than jumping to the Lists page. A line from
  // a project opens the project at that node.
  const originalListId = item.linked_to ? item.linked_list_id : null;
  const fromProject = !!originalListId && lists.find((l) => l.id === originalListId)?.kind === 'project';
  const openOriginal = originalListId
    ? fromProject
      ? () => navigate(projectItemUrl(originalListId, item.linked_to))
      : () => openListDialog(originalListId, item.linked_to)
    : undefined;
  // "+2": the sub-tasks under the original, which the week list doesn't repeat.
  // Counted from what I can see — a private list I'm not on shows no count.
  const moreCount = item.linked_to ? descendantIds(items, item.linked_to).length : 0;

  const [draft, setDraft] = useState(item.text);
  const focusedRef = useRef(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('main');
  // People ticked in the open picker; saved in one write when it closes, so
  // picking three people is one change (and one notification each), not three.
  const [assignDraft, setAssignDraft] = useState<string[] | null>(null);
  const openAssign = () => setAssignDraft([...item.assignees]);
  const closeAssign = useCallback(() => {
    if (assignDraft) {
      const same = assignDraft.length === item.assignees.length && assignDraft.every((a) => item.assignees.includes(a));
      if (!same) updateItem(item.id, { assignees: assignDraft });
    }
    setAssignDraft(null);
  }, [assignDraft, item.assignees, item.id, updateItem]);

  // Last text sent to the server, so Enter, Tab, the typing pause and blur
  // never send the same edit twice.
  const sentRef = useRef(item.text);

  // Someone else's edit lands while we're not typing in this row.
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(item.text);
      sentRef.current = item.text;
    }
  }, [item.text]);

  const save = useCallback(
    (text: string) => {
      if (text === sentRef.current) return;
      sentRef.current = text;
      updateItem(item.id, { text });
    },
    [item.id, updateItem]
  );

  const autosize = () => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useLayoutEffect(autosize, [draft]);

  // Re-fit when the box's width changes, not just its text: a card is first
  // laid out before the masonry gives it its real width, and the window or
  // sidebar can be resized later — either would otherwise leave long lines
  // clipped to the height measured at the old width.
  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      autosize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!props.focusCaret || !textRef.current) return;
    const el = textRef.current;
    el.focus();
    const at = props.focusCaret === 'start' ? 0 : el.value.length;
    el.setSelectionRange(at, at);
    props.onFocused();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusCaret]);

  // Typing saves after a pause, so teammates watching the list see it.
  useEffect(() => {
    if (!focusedRef.current || !draft.trim()) return;
    const timer = setTimeout(() => save(draft.trim()), 900);
    return () => clearTimeout(timer);
  }, [draft, save]);

  const commit = useCallback(() => {
    focusedRef.current = false;
    const text = draft.trim();
    if (!text && !hasChildren && !item.note) {
      // Blank lines are noise; an emptied line goes away.
      deleteItem(item.id);
      return;
    }
    save(text);
  }, [draft, hasChildren, item.id, item.note, save, deleteItem]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!draft.trim()) {
        if (depth > 0) props.onOutdent();
        else el.blur();
        return;
      }
      save(draft.trim());
      props.onEnter();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (draft.trim()) save(draft.trim());
      if (e.shiftKey) props.onOutdent();
      else props.onIndent();
    } else if (e.key === 'Backspace' && draft === '' && !hasChildren) {
      e.preventDefault();
      props.onRemoveEmpty();
    } else if (e.key === 'ArrowUp' && el.selectionStart === 0 && el.selectionEnd === 0) {
      e.preventDefault();
      props.onFocusPrev();
    } else if (e.key === 'ArrowDown' && el.selectionStart === el.value.length) {
      e.preventDefault();
      props.onFocusNext();
    }
  };

  const toggle = () => {
    if (readOnly) return;
    updateItem(item.id, { progress: item.progress >= 100 ? 0 : 100 });
  };

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setPanel('main');
  }, []);

  const done = item.progress >= 100;
  const mineAssignedByOther = item.assignees.includes(me) && item.assigned_by && item.assigned_by !== me;
  const assigneeNames = item.assignees.map((a) => userName(users, a)).join(', ');
  const overdue = !done && item.due_date && item.due_date < today;
  const dueSoon = !done && item.due_date && !overdue && daysBetween(today, item.due_date) <= 1;
  const followUpDue = !done && item.follow_up_date && item.follow_up_date <= today;
  const href = linkUrl(item.link_type, item.link_ref);

  const chips: React.ReactNode[] = [];
  if (href && item.link_type) {
    chips.push(
      <button key="link" type="button" className="tk-chip tk-chip-link" onClick={() => navigate(href)} title={`Open ${LINK_NOUN[item.link_type].toLowerCase()}`}>
        <TaskIcon name="link" size={11} />
        {LINK_NOUN[item.link_type]}: {item.link_label || item.link_ref}
      </button>
    );
  }
  if (item.due_date && !done) {
    chips.push(
      <span key="due" className={`tk-chip ${overdue ? 'tk-chip-danger' : dueSoon ? 'tk-chip-warn' : ''}`} title={`Due ${shortDate(item.due_date)}`}>
        <TaskIcon name="due" size={11} />
        {overdue ? `Overdue · ${shortDate(item.due_date)}` : relativeDay(item.due_date, today)}
      </span>
    );
  }
  chips.push(...planChips(item, costs));
  if (item.waiting_on && !done) {
    chips.push(
      <span key="wait" className={`tk-chip ${followUpDue ? 'tk-chip-info' : 'tk-chip-muted'}`}>
        <TaskIcon name="waiting" size={11} />
        {followUpDue ? 'Follow up' : 'Waiting on'} {item.waiting_on}
        {item.follow_up_date && !followUpDue ? ` · ${shortDate(item.follow_up_date)}` : ''}
      </span>
    );
  }
  if (item.linked_to) {
    if (moreCount > 0) {
      chips.push(
        <button
          key="more"
          type="button"
          className="tk-chip tk-chip-week"
          onClick={openOriginal}
          title={`${moreCount} sub-task${moreCount === 1 ? '' : 's'} under this — click to see them all`}
        >
          +{moreCount}
        </button>
      );
    }
    chips.push(
      <button
        key="from"
        type="button"
        className="tk-chip tk-chip-week"
        onClick={openOriginal}
        title="Open the list it's from — ticking it here ticks its sub-tasks too"
      >
        <TaskIcon name={fromProject ? 'project' : 'list'} size={11} />
        From {item.linked_list_title || 'another list'}
      </button>
    );
  } else if (item.week_copy_list_id && item.week_copy_list_id === weeklyListId) {
    chips.push(
      <button
        key="week"
        type="button"
        className="tk-chip tk-chip-week"
        onClick={() => navigate(`/lists?list=${weeklyListId}`)}
        title="Linked to this week's MPP list — changes show on both"
      >
        <TaskIcon name="pin" size={11} />
        On this week's list
      </button>
    );
  }
  if (props.fold?.collapsed && props.fold.hidden > 0) {
    chips.push(
      <button key="folded" type="button" className="tk-chip tk-chip-muted" onClick={props.fold.onToggle} title="Folded — click to show">
        +{props.fold.hidden}
      </button>
    );
  }
  if (props.moreBelow && props.moreBelow.count > 0) {
    chips.push(
      <button
        key="below"
        type="button"
        className="tk-chip tk-chip-week"
        onClick={props.moreBelow.onOpen}
        title={`${props.moreBelow.count} more line${props.moreBelow.count === 1 ? '' : 's'} under this — open it in the project`}
      >
        <TaskIcon name="project" size={11} />+{props.moreBelow.count}
      </button>
    );
  }
  if (item.carry_count > 0 && depth === 0 && !done) {
    chips.push(
      <span key="carry" className="tk-chip tk-chip-muted" title="Carried over from earlier weeks">
        <TaskIcon name="carry" size={11} />
        {item.carry_count} wk{item.carry_count === 1 ? '' : 's'}
      </span>
    );
  }
  if (done && item.completed_at) {
    chips.push(
      <span key="done" className="tk-chip tk-chip-muted">
        {item.resolved_reason === 'auto'
          ? 'Resolved automatically'
          : `${firstName(users, item.completed_by)} · ${shortDate(dayOfTimestamp(item.completed_at))}`}
      </span>
    );
  }

  const sourceIcons: React.ReactNode[] = [];
  const kind = kindIcon(item);
  if (kind) sourceIcons.push(kind);
  if (item.source === 'auto') sourceIcons.push(<span key="auto" className="tk-src tk-src-auto" title="Created automatically by the ERP"><TaskIcon name="auto" size={13} /></span>);
  if (item.source === 'recurring') sourceIcons.push(<span key="rec" className="tk-src tk-src-recurring" title="Recurring task"><TaskIcon name="recurring" size={13} /></span>);
  if (item.source === 'scheduled') sourceIcons.push(<span key="sch" className="tk-src tk-src-scheduled" title="Scheduled task"><TaskIcon name="scheduled" size={13} /></span>);
  if (item.source === 'request') sourceIcons.push(<span key="req" className="tk-src tk-src-request" title={`Warehouse request from ${userName(users, item.created_by)}`}><TaskIcon name="bell" size={13} /></span>);
  if (mineAssignedByOther) sourceIcons.push(<span key="asg" className="tk-src tk-src-assigned" title={`Assigned to you by ${userName(users, item.assigned_by)}`}><TaskIcon name="assigned" size={13} /></span>);

  // A warehouse request stays "New" for everyone until someone picks it up —
  // makes progress on it or answers with a note.
  const unanswered = item.source === 'request' && depth === 0 && item.progress === 0 && !item.note;
  const showNew = isNew || unanswered;

  return (
    <li
      ref={props.rowRef}
      className={[
        'ti-row',
        `ti-depth-${depth}`,
        done ? 'is-done' : '',
        showNew ? 'is-new' : '',
        flash ? 'is-flash' : '',
        dragging ? 'is-dragging' : '',
        item.note ? 'has-note' : '',
        planClasses(item),
      ].join(' ')}
      data-item-id={item.id}
    >
      {draggable && !readOnly ? (
        <span className="ti-handle" {...props.dragHandleProps} role="button" tabIndex={-1} aria-label={`Drag ${item.text}`} title="Drag to reorder">
          <TaskIcon name="grip" size={14} />
        </span>
      ) : (
        <span className="ti-handle-spacer" />
      )}

      {props.fold &&
        (props.fold.has ? (
          <button
            type="button"
            className={`ti-fold${props.fold.collapsed ? ' is-collapsed' : ''}`}
            onClick={props.fold.onToggle}
            aria-label={props.fold.collapsed ? 'Expand' : 'Collapse'}
            aria-expanded={!props.fold.collapsed}
            title={props.fold.collapsed ? `Show the ${props.fold.hidden} under it` : 'Fold away what\'s under it'}
          >
            <TaskIcon name="chevron-right" size={13} />
          </button>
        ) : (
          <span className="ti-fold-spacer" />
        ))}
      <ProgressCheck value={item.progress} onToggle={toggle} disabled={readOnly} label={`${done ? 'Uncheck' : 'Check'} ${item.text || 'item'}`} />

      <div className="ti-main">
        <div className="ti-line">
          {readOnly ? (
            <span className="ti-text ti-text-static">{item.text}</span>
          ) : (
            <textarea
              ref={textRef}
              className="ti-text"
              rows={1}
              value={draft}
              placeholder="List item"
              onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
              onFocus={() => (focusedRef.current = true)}
              onBlur={commit}
              onKeyDown={onKeyDown}
              aria-label="Item text"
            />
          )}
          {showNew && <span className="ti-new-pill">New</span>}
        </div>
        {chips.length > 0 && <div className="ti-meta">{chips}</div>}
        {item.note && (
          <div className="ti-note-tip" role="tooltip">
            <TaskIcon name="note" size={12} />
            <span>{item.note}</span>
          </div>
        )}
      </div>

      <div className="ti-tail">
        {sourceIcons}
        {item.note && (
          <button
            type="button"
            className="tk-src tk-src-note"
            title={item.note}
            onClick={() => {
              setPanel('note');
              setMenuOpen(true);
            }}
            aria-label="Show note"
          >
            <TaskIcon name="note" size={13} />
          </button>
        )}
        <button
          ref={bubbleRef}
          type="button"
          className={`ti-assignee${item.assignees.length ? '' : ' is-empty'}`}
          onClick={() => !readOnly && openAssign()}
          disabled={readOnly}
          aria-label={item.assignees.length ? `Assigned to ${assigneeNames}` : 'Assign'}
          title={
            item.assignees.length
              ? `${assigneeNames}${item.assigned_by && !item.assignees.every((a) => a === item.assigned_by) ? ` · assigned by ${firstName(users, item.assigned_by)}` : ''}`
              : 'Assign someone'
          }
        >
          {item.assignees.length ? (
            <BubbleStack users={users} emails={item.assignees} />
          ) : (
            <span className="tk-bubble tk-bubble-sm tk-bubble-empty"><TaskIcon name="user" size={11} /></span>
          )}
        </button>
        {!readOnly && (
          <button ref={menuBtnRef} type="button" className="tk-icon-btn ti-more" onClick={() => setMenuOpen(true)} aria-label="Item options">
            <TaskIcon name="more" />
          </button>
        )}
      </div>

      {assignDraft && (
        <Popover anchor={bubbleRef.current} onClose={closeAssign} label="Assign">
          <div className="tk-menu-title">Assign to</div>
          <UserPicker users={users} me={me} value={assignDraft} onChange={setAssignDraft} />
        </Popover>
      )}

      {menuOpen && (
        <Popover anchor={menuBtnRef.current || bubbleRef.current} onClose={closeMenu} label="Item options" width={panel === 'deps' ? 340 : 300}>
          <ItemMenu
            item={item}
            panel={panel}
            setPanel={setPanel}
            hasChildren={hasChildren}
            readOnly={readOnly}
            canAddChild={props.canAddChild ?? item.depth < 2}
            canIndent={props.canIndent}
            canOutdent={props.canOutdent}
            onClose={closeMenu}
            onIndent={props.onIndent}
            onOutdent={props.onOutdent}
            onAddChild={props.onAddChild}
            onOpenLink={href ? () => navigate(href) : undefined}
            onOpenOriginal={openOriginal}
          />
        </Popover>
      )}
    </li>
  );
};

export interface ItemMenuProps {
  item: TaskItem;
  panel: Panel;
  setPanel: (p: Panel) => void;
  hasChildren: boolean;
  readOnly: boolean;
  canAddChild: boolean;
  canIndent?: boolean;
  canOutdent?: boolean;
  onClose: () => void;
  /** Left out on the project map, which moves branches by dragging. */
  onIndent?: () => void;
  onOutdent?: () => void;
  onAddChild: () => void;
  onOpenLink?: () => void;
  onOpenOriginal?: () => void;
}

export type ItemMenuPanel = Panel;

/** The "more" menu for a line, shared by list rows and the project map. */
export const ItemMenu: React.FC<ItemMenuProps> = ({ item, panel, setPanel, hasChildren, readOnly, canAddChild, canIndent = false, canOutdent = false, onClose, onIndent, onOutdent, onAddChild, onOpenLink, onOpenOriginal }) => {
  const { updateItem, deleteItem, today, lists, me, members, costs, weeklyListId, linkToWeek, unlinkFromWeek, showOnLists } = useTasks();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isCopy = !!item.linked_to;
  const itemList = lists.find((l) => l.id === item.list_id);
  const onWeekList = itemList?.kind === 'weekly';
  const inProject = itemList?.kind === 'project';
  // My Lists card for this branch, if I already have one.
  const myView = inProject
    ? lists.find(
        (l) => l.kind === 'view' && l.view_root_item_id === item.id &&
          (l.owner_email === me || (members.get(l.id) || []).includes(me))
      )
    : undefined;
  const onThisWeek = !!item.week_copy_list_id && item.week_copy_list_id === weeklyListId;
  const [note, setNote] = useState(item.note || '');
  const [due, setDue] = useState(item.due_date || '');
  const [waitingOn, setWaitingOn] = useState(item.waiting_on || '');
  const [followUp, setFollowUp] = useState(item.follow_up_date || '');

  // Clicking away (or Escape) keeps what was typed, Keep-style. A button that
  // already saved or removed something marks it handled so closing doesn't
  // write it again.
  const handled = useRef(false);
  const latest = useRef({ panel, note, due, waitingOn, followUp, item });
  latest.current = { panel, note, due, waitingOn, followUp, item };
  useEffect(
    () => () => {
      if (handled.current || readOnly) return;
      const d = latest.current;
      const it = d.item;
      if (d.panel === 'note') {
        const next = d.note.trim() || null;
        if (next !== (it.note || null)) updateItem(it.id, { note: next });
      } else if (d.panel === 'due') {
        if (d.due && d.due !== it.due_date) updateItem(it.id, { due_date: d.due });
      } else if (d.panel === 'waiting') {
        const who = d.waitingOn.trim();
        const follow = d.followUp || null;
        if (who && (who !== it.waiting_on || follow !== it.follow_up_date)) {
          updateItem(it.id, { waiting_on: who, follow_up_date: follow });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const run = (fn: () => void) => () => {
    handled.current = true;
    onClose();
    fn();
  };

  if (panel === 'note') {
    return (
      <div className="tk-menu-panel">
        <div className="tk-menu-title">Note</div>
        <textarea
          className="tk-input tk-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note — shows when you hover over the item"
          autoFocus={!readOnly}
          readOnly={readOnly}
          rows={4}
        />
        {!readOnly && (
          <div className="tk-menu-actions">
            {item.note && <button type="button" className="tk-btn tk-btn-ghost" onClick={run(() => updateItem(item.id, { note: null }))}>Remove</button>}
            <button type="button" className="tk-btn tk-btn-primary" onClick={run(() => updateItem(item.id, { note: note.trim() || null }))}>Save</button>
          </div>
        )}
      </div>
    );
  }

  if (panel === 'plan') {
    return <SchedulePanel item={item} readOnly={readOnly} onClose={() => { handled.current = true; onClose(); }} onOpenDeps={() => setPanel('deps')} />;
  }

  if (panel === 'deps') {
    return <DependsPanel item={item} readOnly={readOnly} onClose={() => { handled.current = true; onClose(); }} onBack={() => setPanel('plan')} />;
  }

  if (panel === 'material') {
    return <MaterialPanel item={item} readOnly={readOnly} onClose={() => { handled.current = true; onClose(); }} />;
  }

  if (panel === 'value') {
    return <ValuePanel item={item} readOnly={readOnly} onClose={() => { handled.current = true; onClose(); }} />;
  }

  if (panel === 'due') {
    return (
      <div className="tk-menu-panel">
        <div className="tk-menu-title">Due date</div>
        <input type="date" className="tk-input" value={due} onChange={(e) => setDue(e.target.value)} autoFocus />
        <div className="tk-quick-dates">
          <button type="button" className="tk-chip-btn" onClick={() => setDue(today)}>Today</button>
          <button type="button" className="tk-chip-btn" onClick={() => setDue(addDays(today, 1))}>Tomorrow</button>
          <button type="button" className="tk-chip-btn" onClick={() => setDue(addDays(today, 7))}>Next week</button>
        </div>
        <div className="tk-menu-actions">
          {item.due_date && <button type="button" className="tk-btn tk-btn-ghost" onClick={run(() => updateItem(item.id, { due_date: null }))}>Clear</button>}
          <button type="button" className="tk-btn tk-btn-primary" disabled={!due} onClick={run(() => updateItem(item.id, { due_date: due }))}>Save</button>
        </div>
      </div>
    );
  }

  if (panel === 'waiting') {
    return (
      <div className="tk-menu-panel">
        <div className="tk-menu-title">Waiting on someone</div>
        <p className="tk-menu-hint">The item moves to "Waiting" and comes back — with a reminder — on the follow-up date.</p>
        <label className="tk-field-label">Waiting on</label>
        <input className="tk-input" value={waitingOn} onChange={(e) => setWaitingOn(e.target.value)} placeholder="Supplier or person" autoFocus />
        <label className="tk-field-label">Follow up on</label>
        <input type="date" className="tk-input" value={followUp} min={today} onChange={(e) => setFollowUp(e.target.value)} />
        <div className="tk-quick-dates">
          <button type="button" className="tk-chip-btn" onClick={() => setFollowUp(addDays(today, 2))}>2 days</button>
          <button type="button" className="tk-chip-btn" onClick={() => setFollowUp(addDays(today, 7))}>1 week</button>
          <button type="button" className="tk-chip-btn" onClick={() => setFollowUp(addDays(today, 14))}>2 weeks</button>
        </div>
        <div className="tk-menu-actions">
          {item.waiting_on && (
            <button type="button" className="tk-btn tk-btn-ghost" onClick={run(() => updateItem(item.id, { waiting_on: null, follow_up_date: null }))}>
              No longer waiting
            </button>
          )}
          <button
            type="button"
            className="tk-btn tk-btn-primary"
            disabled={!waitingOn.trim()}
            onClick={run(() => updateItem(item.id, { waiting_on: waitingOn.trim(), follow_up_date: followUp || null }))}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tk-menu">
      {!hasChildren && (
        <div className="tk-progress-row" role="group" aria-label="Progress">
          {(isMaterial(item)
            ? MATERIAL_STATUS.map((m) => ({ q: m.progress, label: m.label }))
            : QUARTERS.map((q) => ({ q, label: `${q}%` }))
          ).map(({ q, label }) => (
            <button
              key={q}
              type="button"
              className={`tk-progress-opt${item.progress === q ? ' is-selected' : ''}`}
              onClick={run(() => updateItem(item.id, { progress: q }))}
              title={isMaterial(item) ? label : `${q}% done`}
            >
              <ProgressCheck value={q} label={label} size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="tk-menu-item" onClick={() => setPanel('note')}>
        <TaskIcon name="note" size={15} /> {item.note ? 'Edit note' : 'Add note'}
      </button>
      {inProject && !isCopy ? (
        <>
          <button type="button" className="tk-menu-item" onClick={() => setPanel('plan')}>
            <TaskIcon name="gantt" size={15} />
            {item.plan_start && item.plan_finish ? `Schedule · ${rangeLabel(item.plan_start, item.plan_finish)}` : 'Schedule…'}
          </button>
          <button type="button" className="tk-menu-item" onClick={() => setPanel('deps')}>
            <TaskIcon name="waits" size={15} />
            {(item.predecessors || []).length ? `Waits for ${(item.predecessors || []).length}` : 'Waits for…'}
          </button>
          <button type="button" className="tk-menu-item" onClick={() => setPanel('material')}>
            <TaskIcon name="cart" size={15} />
            {isMaterial(item)
              ? `Material${materialTotal(item) !== null ? ` · ${money(materialTotal(item)!)}` : ''}`
              : 'Something to buy…'}
          </button>
          {!isMaterial(item) && (
            <button type="button" className="tk-menu-item" onClick={() => setPanel('value')}>
              <TaskIcon name="value" size={15} />
              {isRanked(valueOf(item, costs)) ? `Value · ${valueLabel(valueOf(item, costs)!)}` : 'Task value…'}
            </button>
          )}
        </>
      ) : (
        <button type="button" className="tk-menu-item" onClick={() => setPanel('due')}>
          <TaskIcon name="due" size={15} /> {item.due_date ? `Due ${shortDate(item.due_date)}` : 'Set due date'}
        </button>
      )}
      <button type="button" className="tk-menu-item" onClick={() => setPanel('waiting')}>
        <TaskIcon name="waiting" size={15} /> {item.waiting_on ? `Waiting on ${item.waiting_on}` : 'Waiting on…'}
      </button>
      <div className="tk-menu-sep" />
      {!isCopy && !onWeekList && (
        onThisWeek ? (
          <button type="button" className="tk-menu-item" onClick={run(() => unlinkFromWeek(item.id))}>
            <TaskIcon name="pin" size={15} /> Take off this week's list
          </button>
        ) : (
          <button type="button" className="tk-menu-item" onClick={run(() => linkToWeek(item.id))}>
            <TaskIcon name="pin" size={15} /> Add to MPP's week list
          </button>
        )
      )}
      {inProject && !isCopy && (
        myView ? (
          <button type="button" className="tk-menu-item" onClick={run(() => navigate(`/lists?list=${myView.id}`))}>
            <TaskIcon name="list" size={15} /> On your Lists — open it
          </button>
        ) : (
          <button
            type="button"
            className="tk-menu-item"
            onClick={run(async () => {
              if (await showOnLists(item.list_id, item.id)) showToast('Added to your Lists', 'success');
            })}
          >
            <TaskIcon name="list" size={15} /> Show on my Lists
          </button>
        )
      )}
      {isCopy && onOpenOriginal && (
        <button type="button" className="tk-menu-item" onClick={run(onOpenOriginal)}>
          <TaskIcon name="list" size={15} /> Open in {item.linked_list_title || 'original list'}
        </button>
      )}
      {(isCopy || !onWeekList) && <div className="tk-menu-sep" />}
      {canAddChild && !isCopy && (
        <button type="button" className="tk-menu-item" onClick={run(onAddChild)}>
          <TaskIcon name="subitem" size={15} /> Add sub-item
        </button>
      )}
      {onIndent && (
        <button type="button" className="tk-menu-item" disabled={!canIndent} onClick={run(onIndent)}>
          <TaskIcon name="indent" size={15} /> Indent <kbd>Tab</kbd>
        </button>
      )}
      {onOutdent && (
        <button type="button" className="tk-menu-item" disabled={!canOutdent} onClick={run(onOutdent)}>
          <TaskIcon name="outdent" size={15} /> Outdent <kbd>⇧Tab</kbd>
        </button>
      )}
      {onOpenLink && (
        <button type="button" className="tk-menu-item" onClick={run(onOpenLink)}>
          <TaskIcon name="link" size={15} /> Open {item.link_type ? LINK_NOUN[item.link_type].toLowerCase() : 'record'}
        </button>
      )}
      <div className="tk-menu-sep" />
      {isCopy ? (
        <button type="button" className="tk-menu-item tk-menu-danger" onClick={run(() => deleteItem(item.id))}>
          <TaskIcon name="close" size={15} /> Remove from this list
        </button>
      ) : (
        <button type="button" className="tk-menu-item tk-menu-danger" onClick={run(() => deleteItem(item.id))}>
          <TaskIcon name="trash" size={15} /> Delete{hasChildren ? ' (with sub-items)' : ''}
          {onThisWeek ? ' — also leaves the week list' : ''}
        </button>
      )}
    </div>
  );
};

export default ItemRow;
